import { useRef, MutableRefObject, useCallback } from 'react';
import { MappingRowExpandIcon } from '../MappingRowExpandIcon';
import { MappingRowDataType } from './../MappingRowDataType';
import { getRowClassNames, MAPPING_ROW_PADDING, getItemClassNames } from '../StepMappingRow/StepMappingRow';
import { DATA_PATH_SEPARATOR, getMappingValidationId, showIconOnDataDescriptionRow } from '../utils/MappingUtils';
import { ValidationIssue } from '@decisions/server/DecisionsFramework';
import { ValidationError } from 'components/inputs/Validation/ValidationError';
import { getValidationSummary } from 'utils/ValidationUtils';
import {
  draggingFromFlowPath,
  getDragTrulyStarted,
  getSelectedFlowNodeId,
  IMappingEditorStore,
  useMappingEditorStore,
  useRowIsHovered,
  MappingPanelType,
  NestedDataDesc,
  useRowPartnerIsHovered,
  getFlowExpandedNodes,
  getDataDescriptionType,
} from '../MappingEditorStore';
import classNames from 'classnames';

interface IFlowMappingRowProps {
  dataDescriptionNode: NestedDataDesc;
  path: string[];
  validationIssues?: Map<string, ValidationIssue[]>;
  isForInput?: boolean;
  isConstantData?: boolean;
}

const FLOW_MAPPING_CLASS_NAME = 'dp-flow-mapping-row';

export const getDataDescRowClassName = (isConstantDataDesc = false) =>
  classNames(FLOW_MAPPING_CLASS_NAME, {
    [`${FLOW_MAPPING_CLASS_NAME}-constant`]: isConstantDataDesc,
  });

/**
 * Particular row in the flow data column
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function FlowMappingRow({
  path,
  dataDescriptionNode,
  validationIssues,
  isForInput,
  isConstantData,
}: IFlowMappingRowProps) {
  const pathString = path.join(DATA_PATH_SEPARATOR);
  const isNodeSelected = useMappingEditorStore(
    useCallback(
      state => {
        const draggingPath = draggingFromFlowPath(state);
        const hasDragStarted = getDragTrulyStarted(state);
        return (draggingPath === pathString && hasDragStarted) || getSelectedFlowNodeId(state) === pathString;
      },
      [pathString]
    )
  );
  const isNodeHovered = useRowIsHovered(pathString, true);
  const isPartnerHovered = useRowPartnerIsHovered(pathString, true);
  const hoverNode = useMappingEditorStore(state => state.hoverNode);
  const hoverClear = useMappingEditorStore(state => state.hoverClear);
  const expandNode = useMappingEditorStore(state => state.expandFlowNode);
  const startDrag = useMappingEditorStore(state => state.startDragConnector);
  const selectNode = useMappingEditorStore(state => state.selectNode);
  const dataDescriptionType = useMappingEditorStore(getDataDescriptionType);
  // use useCallback to memoize a selector that takes an argument. https://github.com/pmndrs/zustand#memoizing-selectors
  const isOpen = useCallback((state: IMappingEditorStore) => getFlowExpandedNodes(state).get(pathString), [pathString]);
  const open = useMappingEditorStore(isOpen);
  const validationIssuesDataDesc = validationIssues?.get(pathString);
  const validationSummary = getValidationSummary(validationIssuesDataDesc ?? []);
  const hasValidation = validationSummary.reasons.length > 0;
  const { validationTooltipId, validationAttributes } = getMappingValidationId(pathString, validationSummary);

  const onExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    expandNode(pathString);
  };

  const flowRowRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  const onRowMouseEnter = () => {
    hoverNode(pathString, MappingPanelType.FLOW);
  };

  const onRowMouseLeave = () => {
    if (isNodeHovered) {
      hoverClear();
    }
  };

  const onRowClick = () => {
    selectNode(pathString, MappingPanelType.FLOW);
  };

  const onMouseDown = () => {
    // ideally we start drag after some mouse-movement delta or mouse leave before a mouse-up,
    startDrag(pathString, MappingPanelType.FLOW);
  };

  return (
    <>
      <div
        className={getRowClassNames(
          getDataDescRowClassName(isConstantData),
          isNodeSelected,
          isNodeHovered || isPartnerHovered
        )}
        ref={flowRowRef}
        onMouseEnter={onRowMouseEnter}
        onMouseLeave={onRowMouseLeave}
        onMouseDown={onMouseDown}
        style={{ paddingLeft: `${path.length - 1 || MAPPING_ROW_PADDING}rem` }}
        onClick={onRowClick}
        {...validationAttributes}
      >
        <MappingRowExpandIcon
          showIcon={showIconOnDataDescriptionRow(dataDescriptionType, dataDescriptionNode)}
          open={open}
          onClick={onExpandClick}
        />
        <div
          className={getItemClassNames(`${FLOW_MAPPING_CLASS_NAME}__label`, hasValidation)}
          title={dataDescriptionNode.DisplayName}
        >
          {dataDescriptionNode.DisplayName}
        </div>
        <MappingRowDataType dataType={`{${dataDescriptionNode.FormattedTypeName}}`} />
      </div>
      {open && (
        <section className={`${FLOW_MAPPING_CLASS_NAME}__children`}>
          {Array.from(dataDescriptionNode.children.values())
            .filter(entry => !entry.filtered)
            .map(child => (
              <FlowMappingRow
                key={child.Name}
                dataDescriptionNode={child}
                path={[...path, child.Name]}
                validationIssues={validationIssues}
                isForInput={isForInput}
              />
            ))}
        </section>
      )}
      {hasValidation && (
        <ValidationError
          id={validationTooltipId}
          sourceRef={flowRowRef.current ?? undefined}
          reasons={validationSummary.reasons}
          levels={validationSummary.levels}
          role="tooltip"
          worstLevel={validationSummary.worstLevel}
          isOpen={isNodeHovered}
          showTooltipAtCenter={true}
          validationTextClassName="dp-mapping-row__label--error"
          placement={'bottom'}
          hideErrorIcon={true}
        />
      )}
    </>
  );
}
